import React from "react";
import { cn } from "../ui";

/**
 * NavChip — the small bordered chip that takes you somewhere.
 *
 * Four call sites wanted the same thing with the same spec: the section rail at
 * the top of the portfolio, a project's Code/Live/Video links, the career
 * profile's GitHub/LinkedIn row, and a certificate's Verify link. Written
 * inline that is the same eighteen utilities four times — which is a component
 * that has not been extracted, not local styling.
 *
 * It lives in this module rather than in `ui/` because it has exactly these
 * four users. If a second module reaches for it, that is the moment to promote
 * it to the barrel — a shared primitive with one consumer is a guess about the
 * future, and the library already carries the cost of one `Button` too many.
 *
 * ⚠️ `tw-border-solid` is not decoration. Preflight is off, so a bare
 * `tw-border` sets a width on a border whose style is still `none` and paints
 * nothing — and on a `<button>` the UA default `outset` is worse than nothing.
 *
 * Renders an `<a>` when given `href`, a `<button>` otherwise. External links get
 * `rel="noopener noreferrer"` here rather than at each call site: these URLs are
 * typed in by students, and a forgotten `rel` at one of four sites is a
 * `window.opener` handed to a page we do not control.
 *
 * Props:
 *   href     — makes it a link; without it, a button
 *   icon     — a lucide component
 *   shape    — "pill" (default) | "chip"  · pill for navigation, chip for a link on a card
 *   size     — "sm" | "xs"
 *   external — open in a new tab (default true for links)
 */
const BASE =
  "tw-inline-flex tw-shrink-0 tw-cursor-pointer tw-items-center tw-font-medium " +
  "tw-border tw-border-solid tw-border-line-soft tw-text-content-muted tw-no-underline " +
  "tw-transition-colors hover:tw-border-primary/40 hover:tw-text-primary tw-focus-ring";

const SHAPES = {
  pill: "tw-rounded-full tw-bg-surface-raised",
  chip: "tw-rounded-md tw-bg-surface-raised",
};

const SIZES = {
  sm: "tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs",
  xs: "tw-gap-1 tw-px-2 tw-py-1 tw-text-[0.72rem]",
};

const NavChip = ({
  href,
  icon: Icon,
  shape = "pill",
  size = "sm",
  external = true,
  children,
  className = "",
  ...props
}) => {
  const classes = cn(BASE, SHAPES[shape] || SHAPES.pill, SIZES[size] || SIZES.sm, className);
  const glyph = Icon ? <Icon size={size === "xs" ? 12 : 13} aria-hidden="true" /> : null;

  if (href) {
    return (
      <a
        href={href}
        className={classes}
        {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        {...props}
      >
        {glyph}
        {children}
      </a>
    );
  }

  return (
    <button type="button" className={classes} {...props}>
      {glyph}
      {children}
    </button>
  );
};

export default NavChip;
